import { screenVehicleHeading } from './vehicleHeading';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { RoutePoint } from '../../../convex/lib/gpsMeasurement';

const ns = 'http://www.w3.org/2000/svg';
type XY = { x: number; y: number };

// Route and vehicle share one projection and one DOM frame. No tile
// quantization, independent marker rounding, or worker update can separate them.
export class ReplayOverlay {
  private root = document.createElement('div');
  private svg = document.createElementNS(ns, 'svg');
  private paths: SVGPathElement[] = [];
  private arrow = document.createElementNS(ns, 'g');
  private heading = 0;
  private moving = true;
  private projected: XY[] = [];
  private lengths: number[] = [];
  private index = 0;
  private coordinate: { lng: number; lat: number };
  constructor(private map: MapboxMap, private element: HTMLElement, private route: RoutePoint[]) {
    this.coordinate = { lng: route[0].longitude, lat: route[0].latitude };
    this.root.className = 'replay-route-overlay';
    Object.assign(this.root.style, { position: 'absolute', inset: '0', pointerEvents: 'none', overflow: 'hidden' });
    Object.assign(this.svg.style, { position: 'absolute', width: '100%', height: '100%', overflow: 'hidden' });
    for (const [stroke, width] of [['rgba(145,180,157,0.16)', '4'], ['rgba(26,26,26,0.5)', '6'], ['rgba(203,218,203,0.9)', '4']]) {
      const path = document.createElementNS(ns, 'path');
      path.setAttribute('fill', 'none'); path.setAttribute('stroke', stroke);
      path.setAttribute('stroke-width', width); path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('stroke-linecap', 'butt');
      this.paths.push(path); this.svg.append(path);
    }
    this.arrow.setAttribute('class', 'replay-vehicle-arrow');
    const arrowPath = document.createElementNS(ns, 'path');
    // Apex (0,0) shares the route's actual SVG coordinate system, including
    // rasterization. A round stroke keeps the zero-area tip visibly connected.
    arrowPath.setAttribute('d', 'M0 0 L11 23 L0 18 L-11 23 Z');
    arrowPath.setAttribute('fill', '#080808');
    arrowPath.setAttribute('stroke', '#e5e5e5');
    arrowPath.setAttribute('stroke-width', '1.2');
    arrowPath.setAttribute('stroke-linejoin', 'round');
    this.arrow.append(arrowPath);
    this.svg.append(this.arrow);
    this.root.append(this.svg, element);
    map.getCanvasContainer().append(this.root);
    map.on('move', this.project); map.on('resize', this.project);
    this.project();
  }
  getElement() { return this.element; }
  getLngLat() { return this.coordinate; }
  setLngLat(coordinate: [number, number]) {
    this.coordinate = { lng: coordinate[0], lat: coordinate[1] };
    this.draw();
    return this;
  }
  setHeading(heading: number, moving: boolean) {
    this.heading = heading; this.moving = moving; this.draw();
  }
  setSegment(index: number) { this.index = index; this.draw(); }
  private project = () => {
    this.projected = this.route.map(p => this.map.project([p.longitude, p.latitude], 0.5));
    this.lengths = [0];
    const commands: string[] = [];
    this.projected.forEach((p, i) => {
      const gap = i === 0 || this.route[i].gapBefore;
      commands.push(`${gap ? 'M' : 'L'}${p.x},${p.y}`);
      if (i) this.lengths.push(this.lengths[i - 1] + (gap ? 0 : Math.hypot(p.x - this.projected[i - 1].x, p.y - this.projected[i - 1].y)));
    });
    for (const path of this.paths) path.setAttribute('d', commands.join(' '));
    this.draw();
  };
  private draw() {
    let p: XY = this.map.project(this.coordinate, 0.5);
    let angle = screenVehicleHeading(this.map, [this.coordinate.lng, this.coordinate.lat], this.heading);
    const a = this.projected[this.index], b = this.projected[this.index + 1];
    const total = this.lengths[this.lengths.length - 1] || 0;
    let progress = this.lengths[this.index] || 0;
    if (a && b && !this.route[this.index + 1].gapBefore) {
      const dx = b.x - a.x, dy = b.y - a.y;
      const length = Math.hypot(dx, dy);
      const along = length ? Math.max(0, Math.min(length, ((p.x - a.x) * dx + (p.y - a.y) * dy) / length)) : 0;
      progress += along;
      if (length) {
        p = { x: a.x + dx * along / length, y: a.y + dy * along / length };
        if (this.moving) angle = Math.atan2(dx, -dy) * 180 / Math.PI;
      }
    }
    this.element.style.transform = `translate(${p.x - 12}px, ${p.y - 12}px)`;
    this.arrow.setAttribute('transform', `translate(${p.x} ${p.y}) rotate(${angle})`);
    this.arrow.style.visibility = this.element.style.visibility;
    for (const path of this.paths.slice(1)) path.setAttribute('stroke-dasharray', `0 ${progress} ${total} ${total}`);
  }
  remove() { this.map.off('move', this.project); this.map.off('resize', this.project); this.root.remove(); }
}
